import {
  createContext,
  type MutableRefObject,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import type { ICockpitExportPayload, ICockpitExportView } from '../../../api'
import { exportCockpitPptx } from '../../../api'

/** Snapshots the expanded tile as it stands the moment the export is asked for. */
type TCockpitExportBuilder = () => ICockpitExportPayload | null

interface ICockpitExportRegistry {
  build: TCockpitExportBuilder | null
  register: (build: TCockpitExportBuilder | null) => void
}

const cockpitExportRegistry = createContext<ICockpitExportRegistry | null>(null)

/**
 * Ties a tile's expanded content to the export button in its header — two sibling subtrees of
 * the tile board, so neither can reach the other by props.
 *
 * The content registers how to snapshot itself; the header shows the button for as long as a
 * registration stands. One registration is all the board ever needs: only one tile is expanded
 * at a time, and only expanded content registers.
 */
export const CockpitExportProvider = ({ children }: { children: ReactNode }) => {
  const [build, setBuild] = useState<TCockpitExportBuilder | null>(null)
  /** Wrapped, because a bare setter would read a builder as a state updater. */
  const register = useCallback(
    (next: TCockpitExportBuilder | null) => setBuild(() => next),
    [setBuild]
  )
  const registry = useMemo(() => ({ build, register }), [build, register])

  return (
    <cockpitExportRegistry.Provider value={registry}>{children}</cockpitExportRegistry.Provider>
  )
}

CockpitExportProvider.displayName = 'CockpitExportProvider'

/**
 * Offers the mounting tile's content for export; `null` withdraws the offer, for a view the tile
 * cannot export. Registers a stable wrapper around the latest `build`, so the board is not
 * re-rendered every time the user regroups or opens a row.
 */
export const useRegisterCockpitExport = (build: TCockpitExportBuilder | null) => {
  const register = useContext(cockpitExportRegistry)?.register
  const latestBuild = useRef(build)
  const canExport = Boolean(build)

  useEffect(() => {
    latestBuild.current = build
  }, [build])

  useEffect(() => {
    if (!register || !canExport) {
      return
    }

    register(() => latestBuild.current?.() ?? null)

    return () => register(null)
  }, [register, canExport])
}

/** The export action for the tile currently offering one. */
export const useCockpitExport = () => {
  const build = useContext(cockpitExportRegistry)?.build ?? null
  const [isExporting, setIsExporting] = useState(false)

  const exportTile = useCallback(async () => {
    const payload = build?.()

    if (!payload) {
      return false
    }

    setIsExporting(true)

    try {
      return await exportCockpitPptx(payload)
    } finally {
      setIsExporting(false)
    }
  }, [build])

  return { canExport: Boolean(build), isExporting, exportTile }
}

/**
 * Where a tile's active view leaves a snapshot of what it renders.
 *
 * A tile knows its filters and which view is on screen; only the view itself knows how its table
 * is grouped, ordered and opened. The slot lets the view report that without its state being
 * lifted, and without the tile re-rendering every time a row is toggled.
 */
export type TCockpitExportSlot = MutableRefObject<(() => ICockpitExportView) | null>

export const useCockpitExportSlot = (): TCockpitExportSlot => useRef(null)

/** Fills the slot for as long as the view is mounted. `snapshot` must be memoized. */
export const useFillCockpitExportSlot = (
  slot: TCockpitExportSlot | undefined,
  snapshot: () => ICockpitExportView
) => {
  useEffect(() => {
    if (!slot) {
      return
    }

    slot.current = snapshot

    return () => {
      if (slot.current === snapshot) {
        slot.current = null
      }
    }
  }, [slot, snapshot])
}
